import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Alert,
} from '@mui/material';
import { Close, Delete, Edit, Save, Cancel } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { zhTW } from 'date-fns/locale';
import { format } from 'date-fns';
import { TimeLog } from '../types';

interface TimeLogModalProps {
  open: boolean;
  onClose: () => void;
  taskTitle: string;
  subtaskText: string;
  subSubtaskText?: string;
  assignees: string[];
  timeLogs: TimeLog[];
  onAddTimeLog: (timeLog: Omit<TimeLog, 'id' | 'createdAt'>) => void;
  onUpdateTimeLog: (timeLogId: string, updates: Partial<TimeLog>) => void;
  onDeleteTimeLog: (timeLogId: string) => void;
}

const TimeLogModal: React.FC<TimeLogModalProps> = ({
  open,
  onClose,
  taskTitle,
  subtaskText,
  subSubtaskText,
  assignees,
  timeLogs,
  onAddTimeLog,
  onUpdateTimeLog,
  onDeleteTimeLog,
}) => {
  const [date, setDate] = useState<Date | null>(new Date());
  const [hours, setHours] = useState<string>('');
  const [assignee, setAssignee] = useState<string>(assignees[0] || '');
  const [description, setDescription] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  const handleSubmit = () => {
    if (!date || !hours || !assignee) {
      setShowError(true);
      return;
    }

    const hoursFloat = parseFloat(hours);
    if (isNaN(hoursFloat) || hoursFloat <= 0) {
      setShowError(true);
      return;
    }

    if (editingId) {
      // 更新現有記錄
      onUpdateTimeLog(editingId, {
        date,
        hours: hoursFloat,
        assignee,
        description,
      });
      setEditingId(null);
    } else {
      // 新增記錄
      onAddTimeLog({
        date,
        hours: hoursFloat,
        assignee,
        description,
      });
    }

    // 重設表單
    setDate(new Date());
    setHours('');
    setDescription('');
    setShowError(false);
  };

  const handleEdit = (log: TimeLog) => {
    setEditingId(log.id);
    setDate(new Date(log.date));
    setHours(log.hours.toString());
    setAssignee(log.assignee);
    setDescription(log.description || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setDate(new Date());
    setHours('');
    setDescription('');
    setShowError(false);
  };

  const handleDelete = (logId: string) => {
    if (window.confirm('確定要刪除此工時記錄嗎？')) {
      onDeleteTimeLog(logId);
    }
  };

  // 計算總工時
  const totalHours = timeLogs.reduce((sum, log) => sum + log.hours, 0);

  // 按人員統計工時
  const hoursByAssignee = timeLogs.reduce((acc, log) => {
    acc[log.assignee] = (acc[log.assignee] || 0) + log.hours;
    return acc;
  }, {} as Record<string, number>);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">工時記錄</Typography>
            <Typography variant="body2" color="text.secondary">
              {taskTitle} / {subtaskText}
              {subSubtaskText && ` / ${subSubtaskText}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* 統計資訊 */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            工時統計
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label={`總工時: ${totalHours} 小時`} color="primary" />
            {Object.entries(hoursByAssignee).map(([person, hours]) => (
              <Chip
                key={person}
                label={`${person}: ${hours} 小時`}
                variant="outlined"
              />
            ))}
          </Box>
        </Box>

        {/* 新增/編輯表單 */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {editingId ? '編輯工時記錄' : '新增工時記錄'}
          </Typography>
          
          {showError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShowError(false)}>
              請填寫所有必要欄位，並確保時數大於 0
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhTW}>
              <DatePicker
                label="日期"
                value={date}
                onChange={(newValue) => setDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              label="時數"
              type="number"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ step: 0.5, min: 0 }}
            />

            <TextField
              select
              label="負責人"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              size="small"
              fullWidth
            >
              {assignees.map((person) => (
                <MenuItem key={person} value={person}>
                  {person}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="備註說明"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={1}
            />
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {editingId && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Cancel />}
                onClick={handleCancelEdit}
              >
                取消
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={editingId ? <Save /> : undefined}
              onClick={handleSubmit}
            >
              {editingId ? '更新' : '新增'}
            </Button>
          </Box>
        </Box>

        {/* 工時記錄列表 */}
        {timeLogs.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>日期</TableCell>
                  <TableCell>負責人</TableCell>
                  <TableCell align="right">時數</TableCell>
                  <TableCell>備註</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {timeLogs
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((log) => (
                    <TableRow
                      key={log.id}
                      sx={{
                        bgcolor: editingId === log.id ? 'action.selected' : undefined,
                      }}
                    >
                      <TableCell>{format(new Date(log.date), 'yyyy/MM/dd')}</TableCell>
                      <TableCell>{log.assignee}</TableCell>
                      <TableCell align="right">{log.hours}</TableCell>
                      <TableCell>{log.description || '-'}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(log)}
                          disabled={editingId !== null}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(log.id)}
                          disabled={editingId !== null}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {timeLogs.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography>尚無工時記錄</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>關閉</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimeLogModal; 